import { AGENT_ANSWER_ABORTED } from '../infrastructure/webrtc/voiceAgentSessionStore';

export const VOICE_ANSWER_ERROR = Object.freeze({
  PROVIDER_ANSWER_FAILED: 'provider_answer_failed',
  TURN_ALLOCATE: 'turn_allocate',
  MEDIA_PERMISSION: 'media_permission',
  WEBRTC_LEG_TIMEOUT: 'webrtc_leg_timeout',
  WEBRTC_MEDIA_FAILED: 'webrtc_media_failed',
  REQUEST_TIMEOUT: 'request_timeout',
  GENERIC: 'generic',
  ABORTED: 'aborted'
});

const WEBRTC_LEG_TIMEOUT_MESSAGE = 'Timed out waiting for agent WebRTC leg';
const WEBRTC_MEDIA_FAILED_MARKER = 'Sin media WebRTC/TURN';

/**
 * Maps thrown values to stable answer-flow error codes (no UX copy here).
 *
 * @param {unknown} error
 * @returns {string} VOICE_ANSWER_ERROR value
 */
export function classifyVoiceAnswerError(error) {
  if (!error) {
    return VOICE_ANSWER_ERROR.GENERIC;
  }

  if (typeof error === 'object') {
    if (error.code === AGENT_ANSWER_ABORTED) {
      return VOICE_ANSWER_ERROR.ABORTED;
    }
    if (error.code === 'agent_webrtc_media_failed') {
      return VOICE_ANSWER_ERROR.WEBRTC_MEDIA_FAILED;
    }
    if (error.code === 'TURN_ALLOCATE') {
      return VOICE_ANSWER_ERROR.TURN_ALLOCATE;
    }
    if (error.code === 'REQUEST_TIMEOUT') {
      return VOICE_ANSWER_ERROR.REQUEST_TIMEOUT;
    }
    const platformCode = error.voiceErrorCode
      ?? (error.body && typeof error.body === 'object' ? error.body?.error?.error : undefined);
    if (platformCode === 'provider_answer_failed') {
      return VOICE_ANSWER_ERROR.PROVIDER_ANSWER_FAILED;
    }
    if (error.status === 502) {
      return VOICE_ANSWER_ERROR.PROVIDER_ANSWER_FAILED;
    }
  }

  const message = error instanceof Error ? error.message : String(error);

  if (message === WEBRTC_LEG_TIMEOUT_MESSAGE) {
    return VOICE_ANSWER_ERROR.WEBRTC_LEG_TIMEOUT;
  }
  if (message.includes(WEBRTC_MEDIA_FAILED_MARKER) || message.includes('ICE/TURN relay did not carry RTP')) {
    return VOICE_ANSWER_ERROR.WEBRTC_MEDIA_FAILED;
  }
  if (message.includes('provider_answer_failed')) {
    return VOICE_ANSWER_ERROR.PROVIDER_ANSWER_FAILED;
  }
  if (
    message.includes('allocate request timed out')
    || message.includes('errorCode: 701')
    || message.includes('TURN allocate')
  ) {
    return VOICE_ANSWER_ERROR.TURN_ALLOCATE;
  }
  if (
    message.includes('getUserMedia')
    || message.includes('Permission')
    || message.includes('ICE connection failed')
    || (message.includes('TURN') && !message.includes('TURN allocate') && !message.includes(WEBRTC_MEDIA_FAILED_MARKER))
  ) {
    return VOICE_ANSWER_ERROR.MEDIA_PERMISSION;
  }
  if (isVoiceAnswerRequestTimeout(error, message)) {
    return VOICE_ANSWER_ERROR.REQUEST_TIMEOUT;
  }

  return VOICE_ANSWER_ERROR.GENERIC;
}

/**
 * @param {unknown} error
 * @param {string} [message]
 */
export function isVoiceAnswerRequestTimeout(error, message = '') {
  if (error && typeof error === 'object' && error.name === 'AbortError') {
    return true;
  }
  const text = message || (error instanceof Error ? error.message : String(error ?? ''));
  return text.includes('(timeout)') || text.toLowerCase().includes('timeout');
}

export function isVoiceProviderAnswerFailed(error) {
  return classifyVoiceAnswerError(error) === VOICE_ANSWER_ERROR.PROVIDER_ANSWER_FAILED;
}
